import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { DataTable, type Column } from '@/components/DataTable'
import { Button } from '@/components/ui/Button'
import { analyticsInsights } from '@/features/assistant/api'
import { AssistantPanel } from '@/features/assistant/AssistantPanel'
import { useAuth } from '@/features/auth/store'
import { AccuracyCard } from '@/features/recommendations/AccuracyCard'
import {
  CHANNEL_LABELS,
  CHANNELS,
  formatMoney,
  formatPct,
  getSalesSummary,
  getTargets,
  rebuildAnalytics,
  type Channel,
  type RepAttainment,
} from '@/features/analytics/api'
import {
  AttainmentBars,
  ChannelSplit,
  ChartCard,
  Kpi,
  RevenueTrend,
  WindowPicker,
} from '@/features/analytics/panels'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

/**
 * Revenue, margin, trend and target attainment.
 *
 * Revenue is shown next to the period before it rather than on its own, because a figure
 * with nothing to compare it to is a fact and not information -- and because the comparison
 * is the one thing a reader would otherwise work out on paper and get wrong.
 *
 * The rebuild button is here and not on a settings screen because this is where somebody
 * notices the figures are stale. It recomputes each day from the orders and the till, so
 * pressing it twice is the same as pressing it once; that is the whole point of the rollup
 * being a recompute rather than an adjustment.
 */
export function SalesAnalytics() {
  const [days, setDays] = useState(30)
  const [channel, setChannel] = useState<Channel | ''>('')
  const canRebuild = useAuth((s) => s.can)('sales_analytics', 'update')
  const queryClient = useQueryClient()

  const sales = useQuery({
    queryKey: ['analytics', 'sales', days, channel],
    queryFn: () => getSalesSummary({ days, channel }),
    retry: false,
  })
  const targets = useQuery({
    queryKey: ['analytics', 'targets'],
    queryFn: getTargets,
    retry: false,
  })

  const rebuild = useMutation({
    mutationFn: () => rebuildAnalytics(90),
    onSuccess: (summary) => {
      toast.success(`Recomputed ${summary.days} day${summary.days === 1 ? '' : 's'} from source.`)
      void queryClient.invalidateQueries({ queryKey: ['analytics'] })
    },
    onError: () => toast.error('Could not rebuild the analytics tables.'),
  })

  const totals = sales.data?.totals
  const previous = sales.data?.previous
  const delta = sales.data?.delta

  const repColumns: Column<RepAttainment>[] = [
    {
      key: 'rep',
      header: 'Sales rep',
      className: 'font-medium',
      sortValue: (row) => row.rep_name ?? '',
      cell: (row) => row.rep_name ?? 'Unassigned',
    },
    {
      key: 'code',
      header: 'Code',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (row) => row.employee_code ?? '—',
    },
    {
      key: 'target',
      header: 'Target',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      sortValue: (row) => row.target,
      cell: (row) => formatMoney(row.target),
    },
    {
      key: 'achieved',
      header: 'Achieved',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      sortValue: (row) => row.achieved,
      cell: (row) => formatMoney(row.achieved),
    },
    {
      key: 'attainment',
      header: 'Attainment',
      className: 'text-right font-medium tabular-nums',
      headerClassName: 'text-right',
      sortValue: (row) => row.attainment_pct ?? -1,
      cell: (row) => formatPct(row.attainment_pct),
    },
  ]

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Sales analytics</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Revenue, margin and how the month is tracking against target.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="sr-only" htmlFor="sales-channel">
            Channel
          </label>
          <select
            id="sales-channel"
            className={SELECT_CLASS}
            value={channel}
            onChange={(event) => setChannel(event.target.value as Channel | '')}
          >
            <option value="">Every channel</option>
            {CHANNELS.map((option) => (
              <option key={option} value={option}>
                {CHANNEL_LABELS[option]}
              </option>
            ))}
          </select>
          <WindowPicker days={days} onChange={setDays} asOf={sales.data?.as_of} />
          {canRebuild && (
            <Button
              variant="secondary"
              onClick={() => rebuild.mutate()}
              disabled={rebuild.isPending}
            >
              {rebuild.isPending ? 'Rebuilding…' : 'Rebuild'}
            </Button>
          )}
        </div>
      </header>

      <div className="grid gap-4 tablet:grid-cols-2 desktop:grid-cols-4">
        <Kpi
          label="Revenue"
          value={formatMoney(totals?.net ?? null)}
          delta={delta?.net_pct ?? null}
          hint={`was ${formatMoney(previous?.net ?? null)}`}
          loading={sales.isLoading}
        />
        <Kpi
          label="Margin"
          value={formatMoney(totals?.margin ?? null)}
          hint={`${formatPct(totals?.margin_pct)} of revenue`}
          loading={sales.isLoading}
        />
        <Kpi
          label="Cost of sales"
          value={formatMoney(totals?.cost ?? null)}
          hint={`${totals?.units ?? 0} units`}
          loading={sales.isLoading}
        />
        <Kpi
          label="Transactions"
          value={totals ? totals.transactions.toLocaleString() : '—'}
          hint={`was ${previous?.transactions ?? 0}`}
          loading={sales.isLoading}
        />
      </div>

      <AssistantPanel
        title="Insights"
        description={`A plain-English read of sales, targets, products and stock for the last ${days} days.`}
        buttonLabel="Summarise"
        ask={() => analyticsInsights(days)}
      />

      <AccuracyCard />

      <ChartCard
        title="Revenue and margin by day"
        description={
          sales.data ? `${sales.data.period_start} to ${sales.data.period_end}` : undefined
        }
        isLoading={sales.isLoading}
        error={sales.error}
        isEmpty={!sales.data?.series.length}
        emptyMessage={`Nothing was sold in the last ${days} days.`}
        height={300}
      >
        <RevenueTrend series={sales.data?.series ?? []} />
      </ChartCard>

      <div className="grid gap-4 desktop:grid-cols-2">
        <ChartCard
          title="Where the revenue came from"
          description="Order book against the till."
          isLoading={sales.isLoading}
          error={sales.error}
          isEmpty={!sales.data?.channels.length}
          emptyMessage="No sales in this window, through either channel."
        >
          <ChannelSplit channels={sales.data?.channels ?? []} />
        </ChartCard>

        <ChartCard
          title="Against target, month to date"
          description="The dashed line is how far through the month we are."
          isLoading={targets.isLoading}
          error={targets.error}
          isEmpty={!targets.data?.reps.length}
          emptyMessage="Nobody you can see carries a monthly sales target."
        >
          <AttainmentBars
            reps={targets.data?.reps ?? []}
            elapsedPct={targets.data?.month_elapsed_pct ?? 0}
          />
        </ChartCard>
      </div>

      <DataTable
        rows={targets.data?.reps ?? []}
        columns={repColumns}
        getRowId={(row) => row.rep_id ?? 'unassigned'}
        caption="Sales target attainment by rep, month to date"
        isLoading={targets.isLoading}
        error={targets.error}
        emptyMessage="Nobody you can see carries a monthly sales target."
      />
    </div>
  )
}
